/**
 * Стартовое наполнение аккаунта.
 *
 * Новый пользователь не должен встречать пустой календарь: методология Kairo
 * начинается с того, что сон и питание уже стоят в расписании, а задачи
 * планируются вокруг них. Поэтому режим дня создаётся сразу при регистрации —
 * его остаётся только подстроить под себя.
 */

import { db } from '@/shared/db/database';
import { createRepository } from '@/shared/db/repository';
import type { Profile, Routine } from '@/shared/db/schema';
import { requireUserId } from '@/shared/db/session-context';

const profilesRepo = createRepository<Profile>('profiles', db.profiles);
const routinesRepo = createRepository<Routine>('routines', db.routines);

/** Все дни недели: биты 0–6. */
const EVERY_DAY = 0b1111111;

/**
 * Режим дня по умолчанию.
 *
 * Значения взяты как усреднённо-здоровые: 8 часов сна и три приёма пищи с
 * интервалом около пяти часов. Три основных приёма помечены `isCore` — их
 * нельзя удалить, только сдвинуть; перекусы пользователь добавляет сам.
 */
export const DEFAULT_ROUTINES: Omit<
  Routine,
  'id' | 'userId' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'revision' | 'syncState'
>[] = [
  {
    kind: 'sleep',
    title: 'Сон',
    startMin: 23 * 60,
    durationMin: 8 * 60, // 23:00 → 07:00, переходит через полночь
    weekdaysMask: EVERY_DAY,
    isCore: true,
    position: 0,
    enabled: true,
  },
  {
    kind: 'meal',
    title: 'Завтрак',
    startMin: 8 * 60,
    durationMin: 30,
    weekdaysMask: EVERY_DAY,
    isCore: true,
    position: 1,
    enabled: true,
  },
  {
    kind: 'meal',
    title: 'Обед',
    startMin: 13 * 60,
    durationMin: 45,
    weekdaysMask: EVERY_DAY,
    isCore: true,
    position: 2,
    enabled: true,
  },
  {
    kind: 'meal',
    title: 'Ужин',
    startMin: 19 * 60,
    durationMin: 40,
    weekdaysMask: EVERY_DAY,
    isCore: true,
    position: 3,
    enabled: true,
  },
];

/**
 * Создаёт профиль и режим дня для нового аккаунта.
 * Идемпотентна: повторный вызов ничего не дублирует.
 */
export async function seedNewUser(name: string): Promise<void> {
  const userId = requireUserId();

  // Проверка идёт по индексу владельца, а не перебором таблицы: в одной
  // локальной базе могут жить несколько аккаунтов, и чужой профиль не должен
  // считаться признаком того, что этот уже создан.
  const existing = await db.profiles.where('userId').equals(userId).toArray();
  if (existing.some((profile) => profile.deletedAt === null)) return;

  await profilesRepo.create({
    name,
    heightCm: null,
    birthDate: null,
    gender: 'unspecified',
    theme: 'system',
    planningLoadFactor: 0.8,
    autoRollover: true,
    stepsGoal: 10_000,
    celebrationsEnabled: true,
    // Включено: разрешение всё равно спрашивается отдельно, а выключатель
    // нужен тому, кому напоминания мешают, — он его и выключит.
    notificationsEnabled: true,
    onboardingCompletedAt: null,
  });

  await routinesRepo.createMany(DEFAULT_ROUTINES);
}
